import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { API } from '../../utils/constants';

const PierLogsModule = ({ onBack, currentUser }) => {
  const [pierLogs, setPierLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [logToDelete, setLogToDelete] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [upcomingJobs, setUpcomingJobs] = useState([]);
  const [newLogForm, setNewLogForm] = useState({
    job_id: '',
  });

  // Fetch pier logs on component mount
  useEffect(() => {
    fetchPierLogs();
    fetchUpcomingJobs();
  }, []);

  const fetchPierLogs = async () => {
    try {
      const response = await axios.get(`${API}/pier-logs`);
      setPierLogs(response.data);
    } catch (error) {
      console.error('Error fetching pier logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs/upcoming-ongoing`);
      setUpcomingJobs(response.data);
    } catch (error) {
      console.error('Error fetching upcoming jobs:', error);
    }
  };

  const handleCreateLog = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/pier-logs`, newLogForm);
      setNewLogForm({ job_id: '' });
      setShowCreateModal(false);
      fetchPierLogs();
      alert('Pier log created successfully!');
    } catch (error) {
      console.error('Error creating pier log:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      const statusCode = error.response?.status || 'Unknown';
      alert(`Error creating pier log (${statusCode}): ${errorMessage}`);
    }
  };

  const handleViewLog = (log) => {
    setSelectedLog(log);
    setShowLogModal(true);
  };

  const handleDeleteLog = (logId) => {
    setLogToDelete(logId);
  };

  const confirmDeleteLog = async () => {
    if (!logToDelete) return;
    try {
      await axios.delete(`${API}/pier-logs/${logToDelete}`);
      await fetchPierLogs();
      setLogToDelete(null);
      alert('Pier log deleted successfully!');
    } catch (error) {
      console.error('Error deleting pier log:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      const statusCode = error.response?.status || 'Unknown';
      alert(`Error deleting pier log (${statusCode}): ${errorMessage}`);
    }
  };

  const cancelDeleteLog = () => {
    setLogToDelete(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <button 
                onClick={onBack}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
              >
                ← Back to Production
              </button>
              <h1 className="text-4xl font-bold text-gray-800">📊 Pier Logs</h1>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2"
            >
              ➕ Start New Pier Log
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="text-xl">Loading pier logs...</div>
          </div>
        ) : (
          <div className="space-y-6">
            {pierLogs.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📊</div>
                <h3 className="text-xl font-semibold mb-2">No Pier Logs Yet</h3>
                <p className="text-gray-600 mb-6">Start tracking pier installation data by creating your first pier log.</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
                >
                  Create First Pier Log
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pierLogs.map((log) => (
                  <div key={log.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-200">
                    <div className="mb-4">
                      <h3 className="font-semibold text-xl text-gray-800 mb-1">{log.job_name}</h3>
                      <p className="text-gray-600">{log.job_address}</p>
                    </div>
                    
                    <div className="text-sm text-gray-500 mb-6 space-y-1">
                      <div>Created by: <span className="text-gray-700">{log.created_by}</span></div>
                      <div>Last modified: <span className="text-gray-700">{new Date(log.last_modified).toLocaleDateString()}</span></div>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => handleViewLog(log)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center gap-2"
                      >
                        📝 View/Edit
                      </button>
                      <button
                        onClick={() => handleDeleteLog(log.id)}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center gap-2"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create New Pier Log Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-6">Create New Pier Log</h2>
            <form onSubmit={handleCreateLog}>
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Select Job</label>
                <select
                  value={newLogForm.job_id}
                  onChange={(e) => setNewLogForm({ ...newLogForm, job_id: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Choose a job...</option>
                  {upcomingJobs.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.display_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Rod length is now configured per pier in the Add New Pier flow */}

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold"
                >
                  Create Pier Log
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-semibold"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Pier Log Confirmation Modal */}
      {logToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h2 className="text-lg font-semibold mb-4">Delete Pier Log</h2>
            <p className="text-sm text-gray-700 mb-6">
              Are you sure you want to delete this pier log? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={cancelDeleteLog}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteLog}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pier Log Detail Modal */}
      {showLogModal && selectedLog && (
        <PierLogDetailModal 
          log={selectedLog} 
          onClose={() => setShowLogModal(false)}
          onRefresh={fetchPierLogs}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

// Pier Log Detail Modal Component  
const PierLogDetailModal = ({ log, onClose, onRefresh, currentUser }) => {
  const [piers, setPiers] = useState(log.piers || []);
  const [rodLength, setRodLength] = useState(log.rod_length ?? 2);
  const [newPierForm, setNewPierForm] = useState({
    location: '',
    starter_rod_depth: '',
    rod_length: String(log.rod_length ?? 2),
  });
  const [showAddPier, setShowAddPier] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [collapsedPiers, setCollapsedPiers] = useState(new Set());
  const [editingPiers, setEditingPiers] = useState(new Set());
  const [editForms, setEditForms] = useState({});

  // Set all piers to collapsed by default when piers data changes
  useEffect(() => {
    if (piers.length > 0) {
      const allPierNumbers = new Set(piers.map(pier => pier.pier_number));
      setCollapsedPiers(allPierNumbers);
    }
  }, [piers.length]);

  // Toggle individual pier collapse/expand
  const togglePierCollapse = (pierNumber) => {
    setCollapsedPiers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pierNumber)) {
        newSet.delete(pierNumber);
      } else {
        newSet.add(pierNumber);
      }
      return newSet;
    });
  };

  // Expand all piers
  const expandAllPiers = () => {
    setCollapsedPiers(new Set());
  };

  // Collapse all piers
  const collapseAllPiers = () => {
    const allPierNumbers = new Set(piers.map(pier => pier.pier_number));
    setCollapsedPiers(allPierNumbers);
  };

  // Start editing pier details
  const startEditingPier = (pier) => {
    setEditingPiers(prev => new Set([...prev, pier.pier_number]));
    setEditForms(prev => ({
      ...prev,
      [pier.pier_number]: {
        location: pier.location,
        starter_rod_depth: pier.starter_rod_depth,
        // use latest known rod length (from backend sync) when entering edit mode
        rod_length: String(rodLength ?? 2),
      }
    }));
  };

  // Cancel editing pier
  const cancelEditingPier = (pierNumber) => {
    setEditingPiers(prev => {
      const newSet = new Set(prev);
      newSet.delete(pierNumber);
      return newSet;
    });
    setEditForms(prev => {
      const newForms = { ...prev };
      delete newForms[pierNumber];
      return newForms;
    });
  };

  // Save pier details
  const savePierDetails = async (pierNumber) => {
    try {
      const formData = editForms[pierNumber];
      if (!formData) return;

      await axios.put(`${API}/pier-logs/${log.id}/piers/${pierNumber}`, {
        location: formData.location,
        starter_rod_depth: parseFloat(formData.starter_rod_depth),
        rod_length: parseFloat(formData.rod_length || rodLength || 2),
      });

      // Refresh pier data
      const response = await axios.get(`${API}/pier-logs/${log.id}`);
      setPiers(response.data.piers);
      // Keep local rodLength in sync with backend
      if (typeof response.data.rod_length === 'number') {
        setRodLength(response.data.rod_length);
      }
      onRefresh();

      // Exit edit mode
      cancelEditingPier(pierNumber);

    } catch (error) {
      console.error('Error updating pier:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Error updating pier: ${errorMessage}`);
    }
  };

  // Update edit form
  const updateEditForm = (pierNumber, field, value) => {
    setEditForms(prev => ({
      ...prev,
      [pierNumber]: {
        ...prev[pierNumber],
        [field]: value
      }
    }));
  };

  // Handle auto-save on Enter or blur
  const handleAutoSave = (pierNumber, event) => {
    if (event.type === 'keydown' && event.key === 'Enter') {
      savePierDetails(pierNumber);
    } else if (event.type === 'blur') {
      // Only auto-save if the form data is different from original
      const originalPier = piers.find(p => p.pier_number === pierNumber);
      const formData = editForms[pierNumber];
      
      if (originalPier && formData && 
          (originalPier.location !== formData.location || 
           originalPier.starter_rod_depth !== parseFloat(formData.starter_rod_depth))) {
        savePierDetails(pierNumber);
      }
    }
  };

  const addPier = async (e) => {
    e.preventDefault();
    try {
      // First create the pier
      const pierResponse = await axios.post(`${API}/pier-logs/${log.id}/piers`, {
        location: newPierForm.location,
        starter_rod_depth: parseFloat(newPierForm.starter_rod_depth),
        rod_length: parseFloat(newPierForm.rod_length || rodLength || 2),
      });
      
      // Get the pier number from the response or calculate it
      const updatedLogResponse = await axios.get(`${API}/pier-logs/${log.id}`);
      const updatedPiers = updatedLogResponse.data.piers;
      const newPierNumber = updatedPiers.length; // The pier we just added
      
      // Automatically add the first rod to this pier
      await axios.post(`${API}/pier-logs/${log.id}/piers/${newPierNumber}/rods`, {
        psi: null // Empty PSI to start
      });
      
      // Refresh the pier log data to show both pier and first rod
      const finalResponse = await axios.get(`${API}/pier-logs/${log.id}`);
      setPiers(finalResponse.data.piers);
      setNewPierForm({ location: '', starter_rod_depth: '', rod_length: newPierForm.rod_length });
      setShowAddPier(false);
      onRefresh();
    } catch (error) {
      console.error('Error adding pier:', error);
      alert('Error adding pier. Please try again.');
    }
  };

  const addRod = async (pierNumber) => {
    try {
      await axios.post(`${API}/pier-logs/${log.id}/piers/${pierNumber}/rods`, {
        psi: null // Empty PSI to start
      });
      
      // Refresh the pier log data
      const response = await axios.get(`${API}/pier-logs/${log.id}`);
      setPiers(response.data.piers);
      onRefresh();
    } catch (error) {
      console.error('Error adding rod:', error);
      alert('Error adding rod. Please try again.');
    }
  };

  const updateRodPsi = async (pierNumber, rodNumber, psi) => {
    try {
      // First get current pier data
      const response = await axios.get(`${API}/pier-logs/${log.id}`);
      const updatedPiers = response.data.piers.map(pier => {
        if (pier.pier_number === pierNumber) {
          pier.rods = pier.rods.map(rod => {
            if (rod.rod_number === rodNumber) {
              rod.psi = psi;
            }
            return rod;
          });
        }
        return pier;
      });

      // Update the pier log with new pier data
      await axios.put(`${API}/pier-logs/${log.id}`, {
        piers: updatedPiers
      });

      setPiers(updatedPiers);
      onRefresh();
    } catch (error) {
      console.error('Error updating rod PSI:', error);
    }
  };

  const deleteRod = async (pierNumber, rodNumber) => {
    try {
      await axios.delete(`${API}/pier-logs/${log.id}/piers/${pierNumber}/rods/${rodNumber}`);

      // Update local state optimistically
      setPiers(prevPiers =>
        prevPiers.map(pier => {
          if (pier.pier_number !== pierNumber) return pier;
          const remainingRods = (pier.rods || []).filter(r => r.rod_number !== rodNumber);
          // Renumber rods locally to match backend behavior
          remainingRods.forEach((r, index) => {
            r.rod_number = index + 1;
          });
          return { ...pier, rods: remainingRods };
        })
      );

      // Also refresh from backend to be 100% in sync
      const response = await axios.get(`${API}/pier-logs/${log.id}`);
      setPiers(response.data.piers);
      onRefresh();
    } catch (error) {
      console.error('Error deleting rod:', error);
      alert('Error deleting rod. Please try again.');
    }
  };

  // Delete entire pier function
  const [pierToDelete, setPierToDelete] = useState(null);

  const requestDeletePier = (pierNumber) => {
    setPierToDelete(pierNumber);
  };

  const deletePier = async () => {
    if (!pierToDelete) return;
    try {
      await axios.delete(`${API}/pier-logs/${log.id}/piers/${pierToDelete}`);

      // Update local state optimistically
      setPiers(prevPiers => {
        const remaining = prevPiers.filter(p => p.pier_number !== pierToDelete);
        // Renumber remaining piers locally to match backend behavior
        return remaining.map((p, index) => ({ ...p, pier_number: index + 1 }));
      });

      // Also refresh from backend to be 100% in sync
      const response = await axios.get(`${API}/pier-logs/${log.id}`);
      setPiers(response.data.piers);
      onRefresh();
      alert('Pier deleted successfully!');
    } catch (error) {
      console.error('Error deleting pier:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Error deleting pier: ${errorMessage}`);
    } finally {
      setPierToDelete(null);
    }
  };

  // Drag and drop handler
  const handleDragEnd = async (result) => {
    // Add error handling for drag operations
    try {
      if (!result.destination) {
        return; // Dropped outside the list
      }

      const sourceIndex = result.source.index;
      const destinationIndex = result.destination.index;

      if (sourceIndex === destinationIndex) {
        return; // No change
      }

      // Create new order array
      const reorderedPiers = Array.from(piers);
      const [removed] = reorderedPiers.splice(sourceIndex, 1);
      reorderedPiers.splice(destinationIndex, 0, removed);

      // Update local state immediately for smooth UX
      setPiers(reorderedPiers);

      // Send new order to backend
      const newOrder = reorderedPiers.map(pier => pier.pier_number);
      
      const response = await axios.put(`${API}/pier-logs/${log.id}/piers/reorder`, {
        pier_order: newOrder
      });

      if (response.status === 200) {
        // Refresh to get updated pier numbers
        const updatedResponse = await axios.get(`${API}/pier-logs/${log.id}`);
        setPiers(updatedResponse.data.piers);
        onRefresh();
      }
    } catch (error) {
      console.error('Error reordering piers:', error);
      // Revert local state on error
      try {
        const response = await axios.get(`${API}/pier-logs/${log.id}`);
        setPiers(response.data.piers);
      } catch (refreshError) {
        console.error('Error refreshing pier data:', refreshError);
      }
      alert('Error reordering piers. Please try again.');
    }
  };

  // PDF Generation Function
  const generatePDF = () => {
    // Basic instrumentation so we know the click handler is firing
    console.log('generatePDF clicked for pier log', log.id);
    setGeneratingPDF(true);

    // Defer heavy jsPDF work so the UI can update first
    setTimeout(() => {
      try {
        // Create new PDF document
        const pdf = new jsPDF();

        // Set font for the document
        pdf.setFont("helvetica");

        // Colors
        const primaryColor = [41, 98, 255]; // Blue
        const secondaryColor = [107, 114, 128]; // Gray
        const textColor = [31, 41, 55]; // Dark gray

        // Header with company branding
        pdf.setFontSize(20);
        pdf.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        pdf.text((currentUser?.company?.name || "").toUpperCase(), 20, 25);

        pdf.setFontSize(16);
        pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
        pdf.text("Pier Installation Report", 20, 35);

        // Job details section
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "bold");
        pdf.text("Job Details:", 20, 50);

        pdf.setFont("helvetica", "normal");
        pdf.text(`Customer: ${log.job_name}`, 20, 60);
        pdf.text(`Address: ${log.job_address}`, 20, 67);
        pdf.text(`Created by: ${log.created_by}`, 20, 74);
        pdf.text(`Rod Length: ${log.rod_length} feet`, 20, 81);
        pdf.text(`Report Generated: ${new Date().toLocaleDateString()}`, 20, 88);

        // Summary statistics
        const totalPiers = piers.length;
        const totalRods = piers.reduce((total, pier) => total + (pier.rods?.length || 0), 0);
        const averageDepth = totalPiers > 0
          ? Math.round(
              (piers.reduce((total, pier) => {
                const finalDepth =
                  pier.rods?.length > 0
                    ? pier.rods[pier.rods.length - 1]?.depth
                    : pier.starter_rod_depth;
                return total + finalDepth;
              }, 0) /
                totalPiers) *
                10
            ) / 10
          : 0;

        const allPsiValues =
          piers.flatMap(
            (pier) =>
              pier.rods?.
                filter(
                  (rod) =>
                    rod.psi &&
                    rod.psi.trim() &&
                    !Number.isNaN(parseFloat(rod.psi))
                )
                .map((rod) => parseFloat(rod.psi)) || []
          );
        const averagePsi =
          allPsiValues.length > 0
            ? Math.round(
                allPsiValues.reduce((sum, psi) => sum + psi, 0) /
                  allPsiValues.length
              )
            : 0;

        pdf.setFont("helvetica", "bold");
        pdf.text("Project Summary:", 20, 105);

        pdf.setFont("helvetica", "normal");
        pdf.text(`Total Piers Installed: ${totalPiers}`, 20, 115);
        pdf.text(`Total Rods Used: ${totalRods}`, 20, 122);
        pdf.text(`Average Final Depth: ${averageDepth} ft`, 20, 129);
        pdf.text(`Average PSI: ${averagePsi}`, 20, 136);

        // Pier details table
        if (piers.length > 0) {
          pdf.setFont("helvetica", "bold");
          pdf.text("Pier Installation Details:", 20, 155);

          let yPos = 170;

          // Table headers
          pdf.setFillColor(240, 240, 240);
          pdf.rect(20, yPos - 5, 170, 10, "F");

          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(10);
          pdf.text("Pier #", 25, yPos);
          pdf.text("Location", 45, yPos);
          pdf.text("Starter Depth", 90, yPos);
          pdf.text("Total Rods", 125, yPos);
          pdf.text("Final Depth", 155, yPos);

          yPos += 15;

          // Table rows
          pdf.setFont("helvetica", "normal");

          piers.forEach((pier, index) => {
            // Check if we need a new page
            if (yPos > 250) {
              pdf.addPage();
              yPos = 30;
            }

            const totalRodsForPier = pier.rods?.length || 0;
            const finalDepthForPier =
              totalRodsForPier > 0
                ? pier.rods[totalRodsForPier - 1]?.depth
                : pier.starter_rod_depth;

            // Alternate row coloring
            if (index % 2 === 0) {
              pdf.setFillColor(248, 250, 252);
              pdf.rect(20, yPos - 5, 170, 10, "F");
            }

            pdf.text(`${pier.pier_number}`, 25, yPos);
            pdf.text(pier.location.substring(0, 20), 45, yPos);
            pdf.text(`${pier.starter_rod_depth} ft`, 90, yPos);
            pdf.text(`${totalRodsForPier}`, 125, yPos);
            pdf.text(`${finalDepthForPier} ft`, 155, yPos);

            yPos += 12;
          });

          // Detailed rod data if space permits
          if (yPos < 200) {
            yPos += 15;
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(12);
            pdf.text("Detailed Rod Data:", 20, yPos);
            yPos += 10;

            pdf.setFontSize(9);

            piers.forEach((pier) => {
              // Check if we need a new page
              if (yPos > 230) {
                pdf.addPage();
                yPos = 30;
              }

              pdf.setFont("helvetica", "bold");
              pdf.text(`Pier ${pier.pier_number} (${pier.location}):`, 20, yPos);
              yPos += 8;

              pdf.setFont("helvetica", "normal");

              if (pier.rods && pier.rods.length > 0) {
                pier.rods.forEach((rod) => {
                  if (yPos > 270) {
                    pdf.addPage();
                    yPos = 30;
                  }

                  const psiText = rod.psi
                    ? `${rod.psi} PSI`
                    : "No PSI recorded";
                  pdf.text(
                    `  Rod ${rod.rod_number}: ${rod.depth} ft - ${psiText}`,
                    25,
                    yPos
                  );
                  yPos += 6;
                });
              } else {
                pdf.text("  No rods recorded", 25, yPos);
                yPos += 6;
              }

              yPos += 5; // Extra space between piers
            });
          }
        }

        // Footer
        const pageCount = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i += 1) {
          pdf.setPage(i);
          pdf.setFontSize(8);
          pdf.setTextColor(
            secondaryColor[0],
            secondaryColor[1],
            secondaryColor[2]
          );
          pdf.text(`Page ${i} of ${pageCount}`, 170, 285);
          pdf.text(
            "Generated by Internal Systems",
            20,
            285
          );
        }

        // Generate filename with job name and date
        const sanitizedJobName = log.job_name.replace(/[^a-zA-Z0-9]/g, "_");
        const dateString = new Date().toISOString().split("T")[0];
        const filename = `Pier_Report_${sanitizedJobName}_${dateString}.pdf`;

        // Try to trigger browser download; if blocked, fall back to opening in new tab
        try {
          pdf.save(filename);
        } catch (saveErr) {
          console.error("Error during pdf.save, falling back to bloburl:", saveErr);
          const blobUrl = pdf.output("bloburl");
          window.open(blobUrl, "_blank", "noopener,noreferrer");
        }
      } catch (error) {
        console.error("Error generating PDF:", error);
        alert("Error generating PDF. Please try again.");
      } finally {
        setGeneratingPDF(false);
      }
    }, 0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold">{log.job_name}</h2>
              <p className="text-gray-600">{log.job_address}</p>
              <p className="text-sm text-gray-500">Rod Length: {log.rod_length} feet | Created by: {log.created_by}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={generatePDF}
                disabled={generatingPDF}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 transition-colors"
              >
                {generatingPDF ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    📄 Download PDF
                  </>
                )}
              </button>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Instructions and Controls */}
            {piers.length > 0 && (
              <div className="space-y-4">
                {/* Drag-drop instructions */}
                {piers.length > 1 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-blue-800">
                      <span className="text-lg">ℹ️</span>
                      <p className="text-sm font-medium">
                        You can drag and drop piers to reorder them. Use the ⋮⋮ handle to drag piers around.
                      </p>
                    </div>
                  </div>
                )}

                {/* Expand/Collapse All Controls */}
                <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                  <div className="text-sm font-medium text-gray-700">
                    Pier Management ({piers.length} pier{piers.length !== 1 ? 's' : ''})
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={expandAllPiers}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm font-medium flex items-center gap-1 transition-colors"
                    >
                      <svg className="w-3 h-3 transform rotate-90" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                      Expand All
                    </button>
                    <button
                      onClick={collapseAllPiers}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm font-medium flex items-center gap-1 transition-colors"
                    >
                      <svg className="w-3 h-3 transform rotate-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                      Collapse All
                    </button>
                  </div>
                </div>
              </div>
            )}

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="pier-list" type="PIER">
                {(provided, snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className={`space-y-4 min-h-[100px] ${snapshot.isDraggingOver ? 'bg-blue-50 rounded-lg p-2' : ''}`}
                  >
                    {piers.map((pier, index) => (
                      <Draggable 
                        key={`pier-${pier.pier_number}-${pier.location}`} 
                        draggableId={`pier-draggable-${pier.pier_number}`} 
                        index={index}
                        isDragDisabled={false}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`bg-gray-50 rounded-lg p-4 transition-shadow ${
                              snapshot.isDragging ? 'shadow-lg ring-2 ring-blue-300 z-10' : 'shadow-sm'
                            }`}
                            style={{
                              ...provided.draggableProps.style,
                              transform: snapshot.isDragging 
                                ? provided.draggableProps.style?.transform 
                                : 'translate(0px, 0px)'
                            }}
                          >
                            <div className="flex justify-between items-center mb-4">
                              <div className="flex items-center gap-3">
                                <div
                                  {...provided.dragHandleProps}
                                  className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-1 select-none"
                                  title="Drag to reorder pier"
                                >
                                  <span className="text-lg font-bold">⋮⋮</span>
                                </div>
                                <button
                                  onClick={() => togglePierCollapse(pier.pier_number)}
                                  className="flex items-center justify-center w-8 h-8 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-all duration-200"
                                  title={collapsedPiers.has(pier.pier_number) ? "Expand pier" : "Collapse pier"}
                                >
                                  <svg 
                                    className={`w-4 h-4 transform transition-transform duration-200 ${collapsedPiers.has(pier.pier_number) ? 'rotate-0' : 'rotate-90'}`}
                                    fill="currentColor" 
                                    viewBox="0 0 20 20"
                                  >
                                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                  </svg>
                                </button>
                                <div>
                                  <h3 className="text-lg font-semibold">
                                    Pier {pier.pier_number}
                                    {pier.rods && pier.rods.length > 0 && (
                                      <span className="text-sm font-normal text-gray-600 ml-2">
                                        ({pier.rods.length} rod{pier.rods.length !== 1 ? 's' : ''})
                                      </span>
                                    )}
                                  </h3>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => startEditingPier(pier)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm font-medium flex items-center gap-1 transition-colors"
                                  title="Edit pier details"
                                >
                                  ✏️ Edit
                                </button>
                                <button
                                  onClick={() => requestDeletePier(pier.pier_number)}
                                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm font-medium flex items-center gap-1 transition-colors"
                                  title="Delete pier and all rods"
                                >
                                  🗑️ Delete
                                </button>
                              </div>
                            </div>
                            
                            {/* Pier Details Section */}
                            <div className="mb-4">
                              {editingPiers.has(pier.pier_number) ? (
                                <div className="space-y-3 bg-blue-50 p-3 rounded border">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-sm font-medium text-blue-800">✏️ Editing Pier {pier.pier_number}</span>
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Location:</label>
                                    <input
                                      type="text"
                                      value={editForms[pier.pier_number]?.location || ''}
                                      onChange={(e) => updateEditForm(pier.pier_number, 'location', e.target.value)}
                                      onKeyDown={(e) => handleAutoSave(pier.pier_number, e)}
                                      onBlur={(e) => handleAutoSave(pier.pier_number, e)}
                                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      placeholder="Enter pier location"
                                      autoFocus
                                    />
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Starter Rod Depth (feet):</label>
                                    <input
                                      type="number"
                                      step="0.1"
                                      value={editForms[pier.pier_number]?.starter_rod_depth || ''}
                                      onChange={(e) => updateEditForm(pier.pier_number, 'starter_rod_depth', e.target.value)}
                                      onKeyDown={(e) => handleAutoSave(pier.pier_number, e)}
                                      onBlur={(e) => handleAutoSave(pier.pier_number, e)}
                                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      placeholder="Enter starter rod depth"
                                    />
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Individual Rod Length (ft):</label>
                                    <select
                                      value={editForms[pier.pier_number]?.rod_length ?? (log.rod_length ?? '2')}
                                      onChange={(e) => updateEditForm(pier.pier_number, 'rod_length', e.target.value)}
                                      onBlur={(e) => handleAutoSave(pier.pier_number, e)}
                                      onKeyDown={(e) => handleAutoSave(pier.pier_number, e)}
                                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                      <option value="1">1 foot</option>
                                      <option value="1.5">1.5 feet</option>
                                      <option value="2">2 feet</option>
                                      <option value="2.5">2.5 feet</option>
                                      <option value="3">3 feet</option>
                                      <option value="3.5">3.5 feet</option>
                                      <option value="4">4 feet</option>
                                      <option value="4.5">4.5 feet</option>
                                      <option value="5">5 feet</option>
                                    </select>
                                  </div>
                                  
                                  <div className="flex gap-2 pt-2">
                                    <button
                                      onClick={() => savePierDetails(pier.pier_number)}
                                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm font-medium flex items-center gap-1"
                                    >
                                      ✓ Save
                                    </button>
                                    <button
                                      onClick={() => cancelEditingPier(pier.pier_number)}
                                      className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm font-medium flex items-center gap-1"
                                    >
                                      ✗ Cancel
                                    </button>
                                  </div>
                                  
                                  <div className="text-xs text-blue-600 mt-2">
                                    💡 Tip: Press Enter or click outside to auto-save
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <p className="text-sm text-gray-600">Location: {pier.location}</p>
                                  <p className="text-sm text-gray-600">Starter Rod Depth: {pier.starter_rod_depth} feet</p>
                                </div>
                              )}
                            </div>

                            {/* Rods Table - Only show when expanded */}
                            {!collapsedPiers.has(pier.pier_number) && (
                              <div className="overflow-x-auto transition-all duration-300 ease-in-out">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="bg-gray-100">
                                      <th className="p-2 text-left">Rod #</th>
                                      <th className="p-2 text-left">PSI</th>
                                      <th className="p-2 text-left">Depth (ft)</th>
                                      <th className="p-2 text-left">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {pier.rods?.map((rod, index) => (
                                      <tr key={rod.rod_number} className="border-b">
                                        <td className="p-2">{rod.rod_number}</td>


                                        <td className="p-2">
                                          <input
                                            type="text"
                                            value={rod.psi || ''}
                                            onChange={(e) => updateRodPsi(pier.pier_number, rod.rod_number, e.target.value)}
                                            className="w-20 p-1 border border-gray-300 rounded"
                                            placeholder="PSI"
                                          />
                                        </td>
                                        <td className="p-2">{rod.depth}</td>
                                        <td className="p-2">
                                          <button
                                            onClick={() => deleteRod(pier.pier_number, rod.rod_number)}
                                            className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs"
                                          >
                                            Delete
                                          </button>
                                        </td>
                                      </tr>
                                    )) || (
                                      <tr>
                                        <td colSpan={4} className="p-4 text-center text-gray-500">
                                          No rods added yet.
                                        </td>
                                      </tr>
                                    )}
                                    {/* Add Next Rod button only after the last rod */}
                                    {pier.rods && pier.rods.length > 0 && (
                                      <tr className="border-b bg-gray-50">
                                        <td colSpan={4} className="p-2 text-center">
                                          <button
                                            onClick={() => addRod(pier.pier_number)}
                                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 rounded text-sm font-medium"
                                          >
                                            + Add Next Rod
                                          </button>
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {/* Collapsed state summary */}
                            {collapsedPiers.has(pier.pier_number) && !editingPiers.has(pier.pier_number) && (
                              <div className="bg-gray-100 rounded p-3 text-sm text-gray-600">
                                <div className="flex justify-between items-center">
                                  <span>
                                    {pier.rods && pier.rods.length > 0 ? (
                                      <>
                                        Last rod depth: {pier.rods[pier.rods.length - 1]?.depth || 'N/A'} ft
                                        {pier.rods.filter(rod => rod.psi && rod.psi.trim()).length > 0 ? (
                                          <span className="ml-3">
                                            PSI readings: {pier.rods.filter(rod => rod.psi && rod.psi.trim()).length}/{pier.rods.length}
                                          </span>
                                        ) : null}
                                      </>
                                    ) : (
                                      'No rods added yet'
                                    )}
                                  </span>
                                  <button
                                    onClick={() => addRod(pier.pier_number)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium"
                                  >
                                    + Add Rod
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {/* Add Pier Button */}
            <div className="text-center mb-6">
              <button
                onClick={() => setShowAddPier(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold"
              >
                + Add New Pier
              </button>
            </div>

            {/* Summary Table */}
            {piers.length > 0 && (
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4 text-blue-800">📊 Pier Summary & Comparison</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm bg-white rounded border">
                    <thead>
                      <tr className="bg-blue-100">
                        <th className="p-3 text-left font-semibold">Pier #</th>
      {pierToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h2 className="text-lg font-semibold mb-4">Delete Pier</h2>
            <p className="text-sm text-gray-700 mb-6">
              Are you sure you want to delete Pier {pierToDelete} and all its rods? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPierToDelete(null)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={deletePier}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

                        <th className="p-3 text-left font-semibold">Location</th>
                        <th className="p-3 text-left font-semibold">Starter Depth</th>
                        <th className="p-3 text-left font-semibold">Total Rods</th>
                        <th className="p-3 text-left font-semibold">Final Depth</th>
                      </tr>
                    </thead>
                    <tbody>
                      {piers.map((pier) => {
                        const totalRods = pier.rods?.length || 0;
                        const finalDepth = totalRods > 0 ? pier.rods[totalRods - 1]?.depth : pier.starter_rod_depth;
                        
                        return (
                          <tr key={pier.pier_number} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">Pier {pier.pier_number}</td>
                            <td className="p-3">{pier.location}</td>
                            <td className="p-3">{pier.starter_rod_depth} ft</td>
                            <td className="p-3">{totalRods} rods</td>
                            <td className="p-3 font-medium">{finalDepth} ft</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {/* Quick Stats */}
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded p-3 text-center">
                    <div className="text-2xl font-bold text-blue-600">{piers.length}</div>
                    <div className="text-sm text-gray-600">Total Piers</div>
                  </div>
                  <div className="bg-white rounded p-3 text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {piers.reduce((total, pier) => total + (pier.rods?.length || 0), 0)}
                    </div>
                    <div className="text-sm text-gray-600">Total Rods</div>
                  </div>
                  <div className="bg-white rounded p-3 text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {piers.length > 0 
                        ? Math.round(piers.reduce((total, pier) => {
                            const finalDepth = pier.rods?.length > 0 
                              ? pier.rods[pier.rods.length - 1]?.depth 
                              : pier.starter_rod_depth;
                            return total + finalDepth;
                          }, 0) / piers.length * 10) / 10
                        : 0} ft
                    </div>
                    <div className="text-sm text-gray-600">Avg Depth</div>
                  </div>
                  <div className="bg-white rounded p-3 text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {(() => {
                        const allPsiValues = piers.flatMap(pier => 
                          pier.rods?.filter(rod => rod.psi && rod.psi.trim() && !isNaN(parseFloat(rod.psi)))
                            .map(rod => parseFloat(rod.psi)) || []
                        );
                        return allPsiValues.length > 0 
                          ? Math.round(allPsiValues.reduce((sum, psi) => sum + psi, 0) / allPsiValues.length)
                          : 0;
                      })()}
                    </div>
                    <div className="text-sm text-gray-600">Avg PSI</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Add Pier Modal */}
      {showAddPier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4">Add New Pier</h2>
            <p className="text-sm text-gray-600 mb-6">This will create the pier and add your first rod so you can immediately start entering PSI data.</p>
            <form onSubmit={addPier}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Pier Location</label>
                <input
                  type="text"
                  value={newPierForm.location}
                  onChange={(e) => setNewPierForm({ ...newPierForm, location: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., Northwest corner"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Starter Rod Depth (ft)</label>
                <input
                  type="number"
                  step="0.1"
                  value={newPierForm.starter_rod_depth}
                  onChange={(e) => setNewPierForm({ ...newPierForm, starter_rod_depth: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., 3.5"
                  required
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Individual Rod Length (ft)</label>
                <select
                  value={newPierForm.rod_length}
                  onChange={(e) => setNewPierForm({ ...newPierForm, rod_length: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="1">1 foot</option>
                  <option value="1.5">1.5 feet</option>
                  <option value="2">2 feet</option>
                  <option value="2.5">2.5 feet</option>
                  <option value="3">3 feet</option>
                  <option value="3.5">3.5 feet</option>
                  <option value="4">4 feet</option>
                  <option value="4.5">4.5 feet</option>
                  <option value="5">5 feet</option>
                </select>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold"
                >
                  Add Rod
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddPier(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-semibold"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Calendar Module Component

const PushPierLogForm = ({ onBack, onSave }) => {
  const [formData, setFormData] = useState({
    crew_leader: '',
    customer_name: '',
    project_number: '',
    date: new Date().toISOString().split('T')[0],
    cylinder_type: 'Red',
    pier_data: {}
  });

  const [isLoading, setIsLoading] = useState(false);

  // Initialize pier data structure
  const tubeData = [
    { name: "Starter", depth: "4' 2\"" },
    { name: "1", depth: "7' 2\"" }, { name: "2", depth: "10' 2\"" }, { name: "3", depth: "13' 2\"" }, { name: "4", depth: "16' 2\"" },
    { name: "5", depth: "19' 2\"" }, { name: "6", depth: "22' 2\"" }, { name: "7", depth: "25' 2\"" }, { name: "8", depth: "28' 2\"" },
    { name: "9", depth: "31' 2\"" }, { name: "10", depth: "34' 2\"" }, { name: "11", depth: "37' 2\"" }, { name: "12", depth: "40' 2\"" },
    { name: "13", depth: "43' 2\"" }, { name: "14", depth: "46' 2\"" }, { name: "15", depth: "49' 2\"" }, { name: "16", depth: "52' 2\"" },
    { name: "17", depth: "55' 2\"" }, { name: "18", depth: "58' 2\"" }, { name: "19", depth: "61' 2\"" }, { name: "20", depth: "64' 2\"" },
    { name: "21", depth: "67' 2\"" }, { name: "22", depth: "70' 2\"" }, { name: "23", depth: "73' 2\"" }, { name: "24", depth: "76' 2\"" },
    { name: "25", depth: "79' 2\"" }, { name: "26", depth: "82' 2\"" }, { name: "27", depth: "85' 2\"" }, { name: "28", depth: "88' 2\"" },
    { name: "Cut Off", depth: "" }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePierDataChange = (tube, pier, value) => {
    setFormData(prev => ({
      ...prev,
      pier_data: {
        ...prev.pier_data,
        [`${tube}_pier_${pier}`]: value
      }
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Here you would normally save to database or generate PDF
      console.log('Saving Push Pier Log data:', formData);
      
      // For now, just show success message
      alert('Push Pier Log data saved successfully!');
      
      onSave(formData);
    } catch (error) {
      console.error('Error saving form:', error);
      alert('Error saving form data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGeneratePDF = async () => {
    setIsLoading(true);
    try {
      // This would generate a filled PDF with the form data
      console.log('Generating filled PDF with data:', formData);
      
      // For now, just show message
      alert('PDF generation feature coming soon!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button onClick={onBack} className="text-blue-600 hover:text-blue-700 text-2xl">← Back</button>
          <h1 className="text-3xl font-bold text-gray-800">PUSH PIER INSTALLATION LOG</h1>
          <div className="flex gap-4">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-bold"
            >
              {isLoading ? 'Saving...' : '💾 Save'}
            </button>
            <button
              onClick={handleGeneratePDF}
              disabled={isLoading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-bold"
            >
              {isLoading ? 'Generating...' : '📄 Generate PDF'}
            </button>
          </div>
        </div>

        {/* Header Information */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-8">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">Project Information</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 font-bold mb-2">Crew Leader:</label>
              <input
                type="text"
                value={formData.crew_leader}
                onChange={(e) => handleInputChange('crew_leader', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter crew leader name"
              />
            </div>
            
            <div>
              <label className="block text-gray-700 font-bold mb-2">Customer Name:</label>
              <input
                type="text"
                value={formData.customer_name}
                onChange={(e) => handleInputChange('customer_name', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter customer name"
              />
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label className="block text-gray-700 font-bold mb-2">Project #:</label>
              <input
                type="text"
                value={formData.project_number}
                onChange={(e) => handleInputChange('project_number', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter project number"
              />
            </div>
            
            <div>
              <label className="block text-gray-700 font-bold mb-2">Date:</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-gray-700 font-bold mb-2">Cylinder Type:</label>
              <div className="flex gap-4 pt-3">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="Red"
                    checked={formData.cylinder_type === 'Red'}
                    onChange={(e) => handleInputChange('cylinder_type', e.target.value)}
                    className="mr-2"
                  />
                  Red
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="Gray"
                    checked={formData.cylinder_type === 'Gray'}
                    onChange={(e) => handleInputChange('cylinder_type', e.target.value)}
                    className="mr-2"
                  />
                  Gray
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Pier Data Table */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">Sections Driven</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 p-2 text-left font-bold">Tube #</th>
                  <th className="border border-gray-300 p-2 text-left font-bold">Depth</th>
                  {Array.from({length: 12}, (_, i) => (
                    <th key={i} className="border border-gray-300 p-2 text-center font-bold text-xs">
                      Pier {i + 1}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tubeData.map((tube, tubeIndex) => (
                  <tr key={tubeIndex} className={tubeIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-300 p-2 font-medium">{tube.name}</td>
                    <td className="border border-gray-300 p-2">{tube.depth}</td>
                    {Array.from({length: 12}, (_, pierIndex) => (
                      <td key={pierIndex} className="border border-gray-300 p-1">
                        <input
                          type="text"
                          value={formData.pier_data[`${tube.name}_pier_${pierIndex + 1}`] || ''}
                          onChange={(e) => handlePierDataChange(tube.name, pierIndex + 1, e.target.value)}
                          className="w-full p-1 text-xs border-0 focus:ring-1 focus:ring-blue-500 text-center"
                          placeholder="..."
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-blue-600">ℹ️</span>
            <h3 className="font-bold text-blue-800">Instructions</h3>
          </div>
          <p className="text-blue-700 text-sm">
            Fill in the sections driven for each pier. Mark with "X" or "✓" for completed sections, 
            or enter specific measurements/notes as needed. Save your progress regularly and generate 
            a PDF when the work is complete.
          </p>
        </div>

        <div className="text-center mt-8">
          <p className="text-gray-600 text-sm">
            Push Pier Installation Log - Mobile-Friendly Web Interface
          </p>
        </div>

        </div>
      </div>
  );
};

export default PierLogsModule;
